/*
 * Queens Play — cart drawer JS (production, optimistic-UI edition).
 *
 * Design goal: dopamine stays high. Every visible cart interaction is
 * instant — no "ADDING…" states, no drawer waiting on a round-trip.
 *
 * Pattern for every mutation (ATC, upsell add, qty +/-, remove):
 *   1. Update UI + cartCache immediately (synthetic line + totals).
 *   2. POST to Shopify in the background.
 *   3. On success — reconcile cache with server response (usually a no-op).
 *   4. On failure — rollback cache + toast the error.
 *
 * Race safety:
 *   - fetchVersion — only the LATEST /cart.js response reconciles cache.
 *     Parallel-ATC race (2 fast clicks) can't leave a stale cache anymore.
 *   - stickerGate re-fires from every mutation's .finally so a mutation
 *     during sticker-in-flight doesn't leave the gate stuck.
 *   - Every fetch checks r.ok — 422 error bodies can't masquerade as carts.
 *   - id-keyed mutations (B-011 safe for multi-variant Remove).
 */
(function () {
  'use strict';

  var overlay = document.querySelector('[data-qp-cart]');
  if (!overlay) return;

  var thresholdDollars = parseFloat(overlay.getAttribute('data-freeship-threshold') || '70');
  var stickerHandle    = overlay.getAttribute('data-sticker-handle') || 'free-laptop-sticker';
  var stickerVariantId = parseInt(overlay.getAttribute('data-sticker-variant-id') || '0', 10);
  var thresholdCents   = thresholdDollars * 100;

  var cartCache      = null;            // last known cart state (drives optimistic UI)
  var fetchVersion   = 0;               // monotonic — only the latest fetchCart response reconciles
  var qtySyncTimers  = {};              // per-line qty debounce timers
  var pendingKeys    = Object.create(null);
  var stickerInFlight = false;
  var drawerOpener   = null;

  var itemsEl  = overlay.querySelector('[data-qp-items]');
  var emptyEl  = overlay.querySelector('[data-qp-empty]');
  var footEl   = overlay.querySelector('[data-qp-foot]');
  var shipEl   = overlay.querySelector('[data-qp-freeship]');
  var subEl    = overlay.querySelector('[data-qp-subtotal]');
  var totalEl  = overlay.querySelector('[data-qp-total]');
  var shipCellEl = overlay.querySelector('[data-qp-shipping]');
  var upsellListEl  = overlay.querySelector('[data-qp-upsell-list]');
  var upsellEmptyEl = overlay.querySelector('[data-qp-upsell-empty]');

  // ---- helpers ----
  function money(cents) { return '$' + (cents / 100).toFixed(2); }
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }
  function toast(msg) {
    var t = document.querySelector('.qp-toast');
    if (!t) {
      t = document.createElement('div');
      t.className = 'qp-toast';
      t.setAttribute('role', 'status');
      t.setAttribute('aria-live', 'polite');
      document.body.appendChild(t);
    }
    t.textContent = msg;
    t.classList.add('show');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(function () { t.classList.remove('show'); }, 2200);
  }
  window.qpToast = toast;

  function liquidRenderedCount() {
    var el = overlay.querySelector('[data-qp-drawer-count]');
    return el ? (parseInt(el.textContent, 10) || 0) : 0;
  }

  // Adopt a server response as the new cache. Guarded by fetchVersion so
  // late responses from earlier fetches can't overwrite fresher data.
  function adoptServerCart(cart, version) {
    if (version != null && version !== fetchVersion) return; // stale, drop
    cartCache = cart;
    pendingKeys = Object.create(null); // server-canonical keys — any temp keys are gone
    renderCart(cart);
  }

  // ---- drawer open/close ----
  function openDrawer(trigger) {
    drawerOpener = trigger || document.activeElement || null;
    overlay.classList.remove('hidden');
    requestAnimationFrame(function () {
      overlay.classList.add('is-open');
      requestAnimationFrame(function () {
        var drawer = overlay.querySelector('.qp-drawer');
        if (drawer) drawer.focus({ preventScroll: true });
      });
    });
    document.body.style.overflow = 'hidden';
    if (!cartCache) reconcile();
  }
  function closeDrawer() {
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(function () { overlay.classList.add('hidden'); }, 200);
    if (drawerOpener && typeof drawerOpener.focus === 'function') {
      drawerOpener.focus();
    }
    drawerOpener = null;
  }
  window.qpOpenCart  = openDrawer;
  window.qpCloseCart = closeDrawer;

  // ---- Shopify /cart APIs ----
  function fetchCart() {
    return fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      cache: 'no-store'
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    });
  }
  function changeLine(id, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: id, quantity: qty })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    });
  }
  function addToCart(variantId, qty) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty || 1 }] })
    }).then(function (r) {
      if (!r.ok) return r.json().then(function (err) { throw err; });
      return r.json();
    });
  }

  // Coalesce reconciles. Fires a fresh /cart.js and lets adoptServerCart
  // discard the response if a newer request has been kicked off since.
  function reconcile() {
    var v = ++fetchVersion;
    return fetchCart().then(function (cart) {
      adoptServerCart(cart, v);
      evaluateStickerGate();
      return cart;
    }).catch(function () { /* silent; next mutation will reconcile */ });
  }

  // ---- optimistic line synthesis ----
  function tempKey(variantId) {
    return 'qp-optimistic-' + Date.now() + '-' + variantId + '-' + Math.random().toString(36).slice(2, 8);
  }
  function synthesizeLine(spec) {
    var opts = [];
    if (spec.product && spec.product.options && spec.variant && spec.variant.options) {
      opts = spec.product.options.map(function (name, i) {
        return { value: spec.variant.options[i] };
      });
    }
    var img = '';
    if (spec.variant && spec.variant.featured_image && spec.variant.featured_image.src) {
      img = spec.variant.featured_image.src;
    } else if (spec.product && spec.product.featured_image) {
      img = spec.product.featured_image;
    } else if (spec.product && spec.product.images && spec.product.images.length) {
      img = spec.product.images[0];
    } else if (spec.thumb) {
      img = spec.thumb;
    }
    var price = spec.priceCents != null ? spec.priceCents : (spec.variant ? spec.variant.price : 0);
    return {
      key: tempKey(spec.variantId),
      product_title: spec.title || (spec.product ? spec.product.title : ''),
      handle: spec.handle || (spec.product ? spec.product.handle : ''),
      quantity: 1,
      final_line_price: price,
      final_price: price,
      url: spec.url || (spec.product ? ('/products/' + spec.product.handle + '?variant=' + spec.variantId) : '#'),
      image: img,
      featured_image: img ? { url: img } : null,
      options_with_values: opts,
      _optimistic: true,
    };
  }

  function applyOptimisticAdd(spec) {
    if (!cartCache) cartCache = { items: [], item_count: 0, total_price: 0 };
    var line = synthesizeLine(spec);
    cartCache.items = (cartCache.items || []).concat([line]);
    cartCache.item_count = (cartCache.item_count || 0) + 1;
    cartCache.total_price = (cartCache.total_price || 0) + line.final_line_price;
    renderCart(cartCache);
    return line;
  }
  function rollbackOptimisticAdd(line) {
    if (!cartCache) return;
    cartCache.items = (cartCache.items || []).filter(function (it) { return it.key !== line.key; });
    cartCache.item_count = Math.max(0, (cartCache.item_count || 1) - 1);
    cartCache.total_price = Math.max(0, (cartCache.total_price || 0) - line.final_line_price);
    renderCart(cartCache);
  }

  /* Public: add-to-cart with instant drawer open.
   * spec: { variantId, variant, product, ... }  (variant+product = Shopify product JSON shape)
   *
   * Race note: bump fetchVersion + prime cartCache BEFORE openDrawer so
   *   (a) initialHydrate's in-flight fetchCart response gets discarded, and
   *   (b) openDrawer sees cartCache != null and skips its warm-cache reconcile.
   * Otherwise either of those fetchCart responses can arrive between the
   * optimistic render and the addToCart POST resolving — server briefly
   * reports "empty cart", adoptServerCart clears the synth line, then the
   * post-add reconcile re-adds it. Symptom: add → remove → add-back flicker.
   */
  window.qpAddToCart = function (spec) {
    if (typeof spec === 'number') spec = { variantId: spec };
    if (!spec || !spec.variantId) return Promise.reject(new Error('no variantId'));

    ++fetchVersion;
    if (!cartCache) cartCache = { items: [], item_count: 0, total_price: 0 };

    // 1. Open drawer INSTANTLY. Inject the synthesized line before any request.
    openDrawer();
    var synth = applyOptimisticAdd(spec);

    // 2. Fire the real request. On success, reconcile via the shared coalescer
    //    — safe against parallel-ATC races (only latest fetchCart wins).
    return addToCart(spec.variantId, 1)
      .then(function () { return reconcile(); })
      .catch(function (err) {
        rollbackOptimisticAdd(synth);
        toast(err && err.description ? err.description : 'Unable to add — try again.');
        throw err;
      });
  };

  // ---- render ----
  function bagCount(cart) {
    var n = 0;
    (cart.items || []).forEach(function (it) {
      if (it.handle === stickerHandle) return;
      n += it.quantity;
    });
    return n;
  }
  function paidTotalCents(cart) {
    var t = 0;
    (cart.items || []).forEach(function (it) {
      if (it.handle === stickerHandle) return;
      t += it.final_line_price;
    });
    return t;
  }
  function updateBagCount(count) {
    document.querySelectorAll('[data-qp-bag-count]').forEach(function (el) {
      el.textContent = count;
      if (count > 0) { el.removeAttribute('hidden'); el.style.display = ''; }
      else { el.setAttribute('hidden', ''); }
    });
    var titleCount = overlay.querySelector('[data-qp-drawer-count]');
    if (titleCount) titleCount.textContent = count;
  }

  function renderShipBar(paidCents, paidCount) {
    if (!shipEl) return;
    var msg  = shipEl.querySelector('.qp-freeship__msg');
    var fill = shipEl.querySelector('.qp-freeship__fill');
    if (!msg || !fill) return;

    if (paidCount === 0) {
      shipEl.classList.remove('is-unlocked');
      msg.textContent = 'Add something to your bag to start earning free shipping ✦';
      fill.style.width = '0%';
      return;
    }
    if (paidCents >= thresholdCents) {
      shipEl.classList.add('is-unlocked');
      msg.textContent = 'FREE SHIPPING UNLOCKED ✦ FREE STICKER ADDED';
      fill.style.width = '100%';
    } else {
      shipEl.classList.remove('is-unlocked');
      var awayCents = thresholdCents - paidCents;
      msg.innerHTML = 'You’re <strong>' + money(awayCents) + '</strong> away from free shipping + your free sticker ✦';
      var pct = Math.max(4, Math.round((paidCents / thresholdCents) * 100));
      fill.style.width = pct + '%';
    }
  }

  function renderTotals(cart, paidCents, paidCount) {
    if (subEl)   subEl.textContent   = money(paidCents);
    if (totalEl) totalEl.textContent = money(paidCents);
    if (shipCellEl) {
      if (paidCount === 0) { shipCellEl.textContent = '—'; shipCellEl.style.color = ''; }
      else if (paidCents >= thresholdCents) { shipCellEl.textContent = 'FREE'; shipCellEl.style.color = 'var(--qp-bubblegum-dk)'; }
      else { shipCellEl.textContent = 'Calculated at checkout'; shipCellEl.style.color = ''; }
    }
  }

  function renderItem(it) {
    var variantParts = (it.options_with_values || []).map(function (o) { return escapeHtml(o.value); }).join(' · ');
    var img = it.featured_image && it.featured_image.url ? it.featured_image.url : (it.image || '');
    var titleEsc = escapeHtml(it.product_title);
    var thumbHtml = img ? '<img src="' + escapeHtml(img) + '" alt="' + titleEsc + '" loading="lazy" width="80" height="100">' : '';
    var variantHtml = variantParts ? '<span class="qp-line__variant">' + variantParts + '</span>' : '';
    var isSticker = it.handle === stickerHandle;
    var stickerClass = isSticker ? ' qp-line--sticker' : '';
    var priceStr = isSticker ? 'FREE' : money(it.final_line_price);
    var qtyDis = (pendingKeys[it.key] || isSticker) ? ' disabled' : '';
    var removeHtml = isSticker
      ? '<button class="qp-line__remove" disabled style="opacity:.5;">Gift</button>'
      : '<button class="qp-line__remove" data-qp-action="remove">Remove</button>';

    return '<article class="qp-line' + stickerClass + '" data-qp-line data-key="' + escapeHtml(it.key) + '">'
      + '<a href="' + escapeHtml(it.url) + '" class="qp-line__media" aria-label="View ' + titleEsc + '">' + thumbHtml + '</a>'
      + '<div class="qp-line__body">'
        + '<h3 class="qp-line__title">' + titleEsc + '</h3>'
        + variantHtml
        + '<div class="qp-line__qty" role="group" aria-label="Quantity for ' + titleEsc + '">'
          + '<button type="button" data-qp-action="qty-dec"' + qtyDis + ' aria-label="Decrease quantity">−</button>'
          + '<span data-qp-qty>' + it.quantity + '</span>'
          + '<button type="button" data-qp-action="qty-inc"' + qtyDis + ' aria-label="Increase quantity">+</button>'
        + '</div>'
      + '</div>'
      + '<div class="qp-line__right">'
        + '<span class="qp-line__price">' + priceStr + '</span>'
        + removeHtml
      + '</div>'
      + '</article>';
  }

  /* Sticker gate — extracted so we can re-run it from every mutation's
   * completion path. Non-blocking: fires the add/remove and returns.
   *
   * Race guard: defer while a user mutation is in flight (pendingKeys
   * non-empty). Otherwise an optimistic renderCart fires the gate against
   * the interim state, sticker POST races the user's own POST, and the
   * user's remove looks like it "came back" when the racing reconcile
   * adopts a pre-remove server view. Every user mutation's `.finally`
   * calls evaluateStickerGate after pendingKeys is cleared so the gate
   * still fires — just against the confirmed server state.
   */
  function evaluateStickerGate() {
    if (stickerInFlight || !cartCache) return;
    for (var k in pendingKeys) { if (pendingKeys[k]) return; }
    var paidCents = paidTotalCents(cartCache);
    var count = bagCount(cartCache);
    var hasSticker = (cartCache.items || []).some(function (it) { return it.handle === stickerHandle; });
    var shouldExist = paidCents >= thresholdCents && count > 0;

    if (shouldExist && !hasSticker && stickerVariantId > 0) {
      stickerInFlight = true;
      addToCart(stickerVariantId, 1)
        .then(function () { return reconcile(); })
        .catch(function () {})
        .then(function () {
          stickerInFlight = false;
          evaluateStickerGate();     // re-check in case cart changed during flight
        });
    } else if (!shouldExist && hasSticker) {
      var stickerLine = (cartCache.items || []).find(function (it) { return it.handle === stickerHandle; });
      if (stickerLine && stickerLine.key) {
        stickerInFlight = true;
        changeLine(stickerLine.key, 0)
          .then(function () { return reconcile(); })
          .catch(function () {})
          .then(function () {
            stickerInFlight = false;
            evaluateStickerGate();
          });
      }
    }
  }

  function renderCart(cart) {
    if (!cart) return;
    var paidCents = paidTotalCents(cart);
    var count = bagCount(cart);

    if (itemsEl) {
      var hasSticker = (cart.items || []).some(function (it) { return it.handle === stickerHandle; });
      if (count === 0 && !hasSticker) {
        itemsEl.innerHTML = '';
        itemsEl.style.display = 'none';
        if (emptyEl) emptyEl.classList.add('is-visible');
        if (footEl)  footEl.classList.add('is-hidden');
      } else {
        itemsEl.style.display = '';
        if (emptyEl) emptyEl.classList.remove('is-visible');
        if (footEl)  footEl.classList.remove('is-hidden');
        itemsEl.innerHTML = (cart.items || []).map(renderItem).join('');
      }
    }

    renderShipBar(paidCents, count);
    renderTotals(cart, paidCents, count);
    updateBagCount(count);
    filterUpsells(cart);

    evaluateStickerGate();
  }

  /* Silent reconcile: called after qty +/- server response. Updates totals
   * + shipping + bag count + specific line price without touching the DOM
   * of other lines. Falls back to renderCart if structure changed.
   */
  function silentReconcile(cart, changedKey) {
    if (!cart) return;
    var domLineKeys = itemsEl ? Array.from(itemsEl.querySelectorAll('.qp-line')).map(function (el) { return el.getAttribute('data-key'); }) : [];
    var cartLineKeys = (cart.items || []).map(function (it) { return it.key; });
    var structuralChange = domLineKeys.length !== cartLineKeys.length
      || domLineKeys.some(function (k) { return cartLineKeys.indexOf(k) === -1; });
    if (structuralChange) { renderCart(cart); return; }

    var paidCents = paidTotalCents(cart);
    var count = bagCount(cart);
    renderShipBar(paidCents, count);
    renderTotals(cart, paidCents, count);
    updateBagCount(count);
    filterUpsells(cart);

    if (changedKey) {
      var line = (cart.items || []).find(function (it) { return it.key === changedKey; });
      var el = itemsEl && itemsEl.querySelector('[data-qp-line][data-key="' + changedKey + '"] .qp-line__price');
      if (line && el) {
        el.textContent = line.handle === stickerHandle ? 'FREE' : money(line.final_line_price);
      }
    }
    evaluateStickerGate();
  }

  function filterUpsells(cart) {
    if (!upsellListEl) return;
    var inCartHandles = (cart.items || []).map(function (it) { return it.handle; });
    var items = Array.from(upsellListEl.querySelectorAll('[data-qp-upsell-item]'));
    var visibleCount = 0;
    items.forEach(function (item) {
      var h = item.getAttribute('data-upsell-handle');
      if (inCartHandles.indexOf(h) !== -1) {
        item.style.display = 'none';
      } else {
        item.style.display = '';
        visibleCount++;
      }
    });
    if (upsellEmptyEl) {
      if (visibleCount === 0) upsellEmptyEl.removeAttribute('hidden');
      else upsellEmptyEl.setAttribute('hidden', '');
    }
  }

  // ---- click delegation ----
  overlay.addEventListener('click', function (e) {
    var t = e.target;

    if (t.closest('[data-qp-cart-close]')) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    // Line qty +/-.
    // ++fetchVersion + pendingKeys[key] MUST be set at CLICK time (before
    // silentReconcile) — silentReconcile fires evaluateStickerGate against
    // the just-mutated optimistic cache, so without pendingKeys the gate
    // would race the qty POST (same B-019 pattern as the Remove flow).
    var qtyBtn = t.closest('[data-qp-action="qty-inc"], [data-qp-action="qty-dec"]');
    if (qtyBtn && !qtyBtn.disabled) {
      e.preventDefault();
      var line = qtyBtn.closest('[data-qp-line]');
      if (!line) return;
      var key  = line.getAttribute('data-key');
      var qtySpan = line.querySelector('[data-qp-qty]');
      if (!qtySpan) return;
      var current = parseInt(qtySpan.textContent, 10) || 1;
      var next = qtyBtn.getAttribute('data-qp-action') === 'qty-inc' ? current + 1 : Math.max(0, current - 1);
      qtySpan.textContent = next;
      ++fetchVersion;
      pendingKeys[key] = true;
      if (cartCache) {
        var cachedLine = (cartCache.items || []).find(function (it) { return it.key === key; });
        if (cachedLine) {
          var unit = cachedLine.final_line_price / Math.max(1, cachedLine.quantity);
          cachedLine.quantity = next;
          cachedLine.final_line_price = unit * next;
          silentReconcile(cartCache, key);
        }
      }
      debouncedSyncQty(key, next);
      return;
    }

    // Line remove — optimistic.
    // fetchVersion++ upfront invalidates any in-flight /cart.js so a
    // racing response can't restore the removed line (same pattern as
    // qpAddToCart, B-014). Sticker gate is skipped while pendingKey is
    // set (see evaluateStickerGate) — fires explicitly after clear.
    var removeBtn = t.closest('[data-qp-action="remove"]:not([disabled])');
    if (removeBtn) {
      e.preventDefault();
      var lineEl = removeBtn.closest('[data-qp-line]');
      if (!lineEl) return;
      var lineKey = lineEl.getAttribute('data-key');
      ++fetchVersion;
      pendingKeys[lineKey] = true;
      lineEl.style.transition = 'opacity 150ms ease';
      lineEl.style.opacity = '0';
      if (cartCache) {
        var removed = (cartCache.items || []).find(function (it) { return it.key === lineKey; });
        if (removed) {
          cartCache.items = cartCache.items.filter(function (it) { return it.key !== lineKey; });
          cartCache.item_count = Math.max(0, (cartCache.item_count || 1) - removed.quantity);
          cartCache.total_price = Math.max(0, (cartCache.total_price || 0) - removed.final_line_price);
          setTimeout(function () { renderCart(cartCache); }, 150);
        }
      }
      changeLine(lineKey, 0)
        .then(function () { return reconcile(); })
        .catch(function () {
          toast('Unable to remove — try again.');
          lineEl.style.opacity = '';
        })
        .then(function () {
          delete pendingKeys[lineKey];
          evaluateStickerGate();
        });
      return;
    }

    // Upsell ADD — either opens the inline size picker (multi-size product)
    // or fires an optimistic add (single-variant / no size option).
    var upAdd = t.closest('[data-qp-upsell-add]');
    if (upAdd && !upAdd.disabled) {
      e.preventDefault();
      var item = upAdd.closest('[data-qp-upsell-item]');
      if (!item) return;

      if (item.getAttribute('data-upsell-needs-size') === '1') {
        // Show inline size picker. Rendering is pre-baked in Liquid; we
        // just toggle visibility + collapse any other upsell that had a
        // picker open (so the drawer doesn't get two pickers at once).
        overlay.querySelectorAll('[data-qp-upsell-item].is-picking').forEach(function (el) {
          if (el !== item) {
            el.classList.remove('is-picking');
            var sizes = el.querySelector('[data-qp-upsell-sizes]');
            if (sizes) sizes.setAttribute('hidden', '');
          }
        });
        item.classList.add('is-picking');
        var sizesEl = item.querySelector('[data-qp-upsell-sizes]');
        if (sizesEl) sizesEl.removeAttribute('hidden');
        var firstSizeBtn = item.querySelector('[data-qp-upsell-size]');
        if (firstSizeBtn) firstSizeBtn.focus();
        return;
      }

      var variantId = parseInt(item.getAttribute('data-upsell-variant-id'), 10);
      if (!variantId) return;
      addUpsellVariant(item, variantId, null);
      return;
    }

    // Upsell size picker — user picked a size, fire the optimistic add.
    var sizeBtn = t.closest('[data-qp-upsell-size]');
    if (sizeBtn && !sizeBtn.disabled) {
      e.preventDefault();
      var sizeItem = sizeBtn.closest('[data-qp-upsell-item]');
      if (!sizeItem) return;
      var sizeVariantId = parseInt(sizeBtn.getAttribute('data-variant-id'), 10);
      if (!sizeVariantId) return;
      var sizePriceRaw = sizeBtn.getAttribute('data-variant-price-cents');
      var sizePriceCents = sizePriceRaw ? parseInt(sizePriceRaw, 10) : null;
      addUpsellVariant(sizeItem, sizeVariantId, sizePriceCents);
      return;
    }

    // Upsell size picker — user cancelled, collapse the picker.
    var cancelBtn = t.closest('[data-qp-upsell-cancel]');
    if (cancelBtn) {
      e.preventDefault();
      var cancelItem = cancelBtn.closest('[data-qp-upsell-item]');
      if (!cancelItem) return;
      cancelItem.classList.remove('is-picking');
      var cancelSizesEl = cancelItem.querySelector('[data-qp-upsell-sizes]');
      if (cancelSizesEl) cancelSizesEl.setAttribute('hidden', '');
      var cancelAddBtn = cancelItem.querySelector('[data-qp-upsell-add]');
      if (cancelAddBtn) cancelAddBtn.focus();
      return;
    }
  });

  /* Shared upsell → cart flow. Called from either the single-variant ADD
   * path or from a size-button click. Handles optimistic-add + rollback +
   * pre-hides the whole card so it can't be re-clicked mid-flight.
   * priceCentsOverride is used when adding a specific size that may cost
   * differently than the first-available variant surfaced in Liquid.
   */
  function addUpsellVariant(item, variantId, priceCentsOverride) {
    var nameEl = item.querySelector('.qp-upsell__name');
    var priceEl = item.querySelector('.qp-upsell__price');
    var thumbImg = item.querySelector('.qp-upsell__thumb img');
    var name = nameEl ? nameEl.textContent.trim() : '';
    var priceCents;
    if (priceCentsOverride != null && !isNaN(priceCentsOverride)) {
      priceCents = priceCentsOverride;
    } else {
      var priceText = priceEl ? priceEl.textContent.trim() : '';
      priceCents = Math.round(parseFloat(priceText.replace(/[^0-9.]/g, '')) * 100);
    }
    if (!priceCents || isNaN(priceCents)) return; // guard: never inject a $0 line

    var thumbSrc = thumbImg ? thumbImg.src : '';
    var handle = item.getAttribute('data-upsell-handle');

    ++fetchVersion; // invalidate any in-flight fetches so racing responses can't stomp the optimistic add
    item.style.display = 'none'; // prevent double-click while add is in-flight

    var synth = applyOptimisticAdd({
      variantId: variantId,
      title: name,
      handle: handle,
      priceCents: priceCents,
      thumb: thumbSrc,
      url: '/products/' + handle,
    });

    addToCart(variantId, 1)
      .then(function () { return reconcile(); })
      .catch(function (err) {
        rollbackOptimisticAdd(synth);
        item.style.display = '';
        item.classList.remove('is-picking');
        var sizesEl = item.querySelector('[data-qp-upsell-sizes]');
        if (sizesEl) sizesEl.setAttribute('hidden', '');
        toast(err && err.description ? err.description : 'Unable to add.');
      });
  }

  // ---- debounced qty sync ----
  // fetchVersion + pendingKeys[key] are set at click time (see qty +/-
  // handler), NOT here — otherwise rapid clicks would fire optimistic
  // renders + sticker gate before pendingKeys got set on the next click.
  function debouncedSyncQty(key, qty) {
    if (qtySyncTimers[key]) clearTimeout(qtySyncTimers[key]);
    qtySyncTimers[key] = setTimeout(function () {
      changeLine(key, qty)
        .then(function () { return reconcile(); })
        .catch(function () { toast('Update failed — retrying.'); })
        .then(function () {
          delete pendingKeys[key];
          delete qtySyncTimers[key];
          evaluateStickerGate();
        });
    }, 220);
  }

  // ---- global triggers ----
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-qp-cart-trigger]');
    if (trigger) {
      e.preventDefault();
      openDrawer(trigger);
    }
  });

  // Tab-cycle focus stays inside the drawer while open (a11y).
  function trapDrawerFocus(e) {
    var focusables = overlay.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    var first = focusables[0];
    var last  = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  document.addEventListener('keydown', function (e) {
    if (!overlay.classList.contains('is-open')) return;
    if (e.key === 'Escape') closeDrawer();
    else if (e.key === 'Tab') trapDrawerFocus(e);
  });

  // Two-tab safety: when this tab becomes visible again, silently reconcile
  // in case the customer added to cart in another tab. reconcile() coalesces
  // + version-guards so back-to-back visibility swaps don't hammer the API.
  // Skip if a user mutation is in flight — the mutation's own reconcile will
  // fire soon and firing an extra one now can adopt a pre-mutation server
  // view (POST hasn't landed yet) and re-render the just-removed line.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    for (var k in pendingKeys) { if (pendingKeys[k]) return; }
    if (stickerInFlight) return;
    reconcile();
  });

  // ---- silent hydration on load ----
  // Warm cartCache in the background. Only re-render if the cart drifted
  // from the Liquid first paint (e.g., mutated in another tab).
  (function initialHydrate() {
    var v = ++fetchVersion;
    fetchCart().then(function (cart) {
      if (v !== fetchVersion) return;
      cartCache = cart;
      pendingKeys = Object.create(null);
      if (cart.item_count !== liquidRenderedCount()) renderCart(cart);
      evaluateStickerGate();
    }).catch(function () {});
  })();

})();
